/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useState, useEffect } from 'react'
import { USBGrid, USBColumn } from '@usb-shield/react-grid'
import { useRouter } from 'next/router'
import { AuthContext } from '../../context/AuthContext'
import PageWarning from '../../components/atoms/PageWarning/PageWarning'
import useLoginHook from '../../hooks/LoginHook'
import useFieldsStateHook from '../../hooks/FieldsStateHook'
import PageLoading from '../../components/atoms/PageLoading/PageLoading'
import {
  isNullOrEmpty,
  hasConnectRole,
  updateObject,
  getSharedCookieDomain,
  isLoginEnabled,
  ScrollTop,
  externalApplicationUrls,
  applicationIds,
  isTrakkerNavigationEnabled,
  isTrakkerSsoHandoffEnabled
} from '../../utils/Utility'
import { loginFieldsState } from '../../utils/FieldsUtility'
import { ValidateSubmit } from '../../utils/LoginUtility'
import { ErrorMessages, NotificationVariant } from '../../utils/messages'
import PageTitle from '../../components/atoms/PageTitle/PageTitle'
import LoginComponent from './LoginComponent'
import CredentialRecoveryPanel from './CredentialRecoveryPanel'
import Content from '../../components/hoc/LayOut/Content'
import useFeaturesHook from "../../hooks/FeaturesHook";
import authenticateAxios from '../../utils/AuthenticateAxios'
/**
 * Login page
 */
const Login = () => {
  const authContext = useContext(AuthContext)
  const { clearSession, logout, login, isUserTimedOut, isUserLoggedOut } = authContext
  const { fieldsState, setFieldState, setFieldsState } = useFieldsStateHook(loginFieldsState)
  const [credentialResetCounter, setCredentialResetCounter] = useState(0)
  const [notificationText, setNotificationText] = useState('')
  const [isCredentialRecovery, setCredentialRecovery] = useState(false)
  const [notificationVariant, setNotificationVariant] = useState(NotificationVariant.confirmation)
  const [loginErrors, setLoginErrors] = useState(null)
  const { isAuthenticating, userInfo, userInfoError, authenticateUser } = useLoginHook()
  const [showNotification, setShowNotification] = useState(
    isUserTimedOut || isUserLoggedOut
  )
  const { features, getFeatures } = useFeaturesHook('')
  const router = useRouter()
  const [passwordValues, setPasswordValues] = useState({
    credential: '',
    confirmCredential: ''
  })

  const clearPasswordValues = useCallback(() => {
    setPasswordValues({
      credential: '',
      confirmCredential: ''
    })
    setCredentialResetCounter(counter => counter + 1)
  }, [])

  const setSensitiveCredentialValue = useCallback((fieldId, value) => {
    setPasswordValues(prevState => ({
      ...prevState,
      [fieldId]: value
    }))
  }, [])

  const getPasswordValue = useCallback(
    fieldId => passwordValues[fieldId] || '',
    [passwordValues]
  )
  const getCredentialCompareToValue = useCallback(
    () => getPasswordValue('credential'),
    [getPasswordValue]
  )

  const navigateToUserProfile = (userdata) => {
    const credentialExpiryTime = userdata && userdata['credentialExpirationTime']

    if (userdata && (!userdata.qid || userdata.qid === '' || !credentialExpiryTime || new Date(credentialExpiryTime) <= new Date(userdata.dateTime)  || !userdata.firstName || userdata.firstName === '' || !userdata.lastName || userdata.lastName === '' || !userdata.emailAddress || userdata.emailAddress.length === 0 || !userdata.emailAddress[0]))
      return true

    return false
  }

  const navigateToTrakker = (userdata) => {

    if (userdata && (userdata.applicationId && (userdata.applicationId.toLowerCase() === applicationIds.Trakker.toLowerCase() || userdata.applicationId.toLowerCase() === applicationIds.TrakkerFI.toLowerCase() || userdata.applicationId.toLowerCase() === applicationIds.TrakkerMerchant.toLowerCase())))
      return true

    return false
  }

  /**
   * Helper method for development only
   */
  useEffect(() => {
    /* TODO: remove this setting user when going to prod  */
    if (getSharedCookieDomain() === 'localhost') {
      const newState = updateObject(fieldsState, {
        companyId: updateObject(fieldsState.companyId, { value: 'EL0018' }),
        userId: updateObject(fieldsState.userId, { value: 'mandadi018' })
        // password: updateObject(fieldsState.password, { value: 'connect28' })
      })
      setFieldsState(newState)
    }
  }, [])
  /**
   * Triggers when the dependency value changes
   */
  useEffect(() => {
    if (isUserTimedOut) {
      setNotificationText(ErrorMessages.sessionExpired)
      setNotificationVariant(NotificationVariant.warning)
    }
    if (isUserLoggedOut) {
      setNotificationText(ErrorMessages.loggedOutByUser)
      setNotificationVariant(NotificationVariant.confirmation)
    }

    if (loginErrors !== null && loginErrors.length > 0) {
      setShowNotification(false)
      setNotificationText('')
    }
    if (
      isUserTimedOut ||
      isUserLoggedOut ||
      (loginErrors !== null && loginErrors.length > 0)
    ) {
      clearSession()
    }
  }, [isUserTimedOut, isUserLoggedOut, loginErrors])

  /**
   * Triggers when the dependency value changes
   */
  useEffect(() => {
    if (isNullOrEmpty(userInfoError) !== '') {
      logout({ isUserLoggedOut: false, isUserTimedOut: false })
      setNotificationText(ErrorMessages.invalidCredentials)
      setNotificationVariant(NotificationVariant.warning)
      setShowNotification(true)
    }
  }, [userInfoError])

  useEffect(() => {
    if (userInfo && isNullOrEmpty(userInfo.userId) !== '') {
      getFeatures();
    }
  }, [userInfo])

  /**
   * Triggers when the dependency value changes
   */
  useEffect(() => {
    if (features && userInfo) {
      // validate if user has connect role
      if (hasConnectRole(userInfo.securityEquals, '|')) {
        login({ ...userInfo, features, isAuthenticated: true }, router.route.startsWith('/Login'))

        if (navigateToUserProfile(userInfo))
          router.push('/UserProfile')
        else if (navigateToTrakker(userInfo) && isTrakkerNavigationEnabled()) {
          // When the TrakkerSsoHandoff feature is enabled, obtain a one-time SSO handoff URL from
          // the API (which no longer relies on shared cross-subdomain cookies); otherwise fall back
          // to the legacy direct navigation. Any handoff failure falls back to the legacy URL.
          if (isTrakkerSsoHandoffEnabled(features)) {
            authenticateAxios.trakkerHandoff()
              .then(res => {
                window.location.href = (res && res.data && res.data.redirectUrl) || externalApplicationUrls.trakker
              })
              .catch(() => {
                window.location.href = externalApplicationUrls.trakker
              })
          } else {
            window.location.href = externalApplicationUrls.trakker
          }
        }
        // router.push(externalApplicationUrls.trakker)

      } else {
        setNotificationText(ErrorMessages.notAuthorized)
        setNotificationVariant(NotificationVariant.warning)
        setShowNotification(true)
        clearSession()
      }
    }
  }, [features])

  /**
   * Validates page and attempts to login
   */
  const loginOrSave = type => {
    if (typeof window !== 'undefined') {
      setShowNotification(false)
      setNotificationText('')
      setLoginErrors(null)

      const errorsLength = ValidateSubmit(
        fieldsState,
        setFieldsState,
        setLoginErrors,
        type,
        passwordValues
      )
      if (errorsLength === 0) {
        if (type === 'login') {
          authenticateUser(
            fieldsState.companyId.value,
            fieldsState.userId.value,
            getPasswordValue('credential')
          )
        } else {
          setNotificationText('Forgot Password is not implemented yet')
          setNotificationVariant(NotificationVariant.warning)
          setShowNotification(true)
          setCredentialRecovery(false)
          clearPasswordValues()
          setFieldState('credential', { changed: false, isError: false })
          setFieldState('confirmCredential', { changed: false, isError: false, isPasswordMismatch: false })
        }
      }
    }
  }

  /**
   * Triggers when user clicks login button
   */
  const onLoginClick = () => {
    // event.preventDefault()
    loginOrSave('login')
  }

  /**
   * Clears state and errors
   */
  const onClearClick = () => {
    setFieldsState(loginFieldsState)
    clearPasswordValues()
    setShowNotification(false)
    setLoginErrors([])
  }

  /**
   * Clears state and errors
   */
  const onCancelClick = () => {
    onClearClick()
    setCredentialRecovery(false)
  }

  const onCredentialRecoveryClick = () => {
    // retain user entered values, but clear errors
    // eslint-disable-next-line array-callback-return
    Object.keys(loginFieldsState).map(key => {
      const field = fieldsState[key]
      field.isError = false
    })
    setShowNotification(false)
    setLoginErrors([])
    clearPasswordValues()
    setCredentialRecovery(true)
  }

  const onSaveClick = () => {
    loginOrSave('save')
  }

  /**
   * Helper method for easy logging in, using only for development
   */
  // const onUserChange = (index, user, isChecked) => {
  //   if (isChecked) {
  //     const newState = updateObject(fieldsState, {
  //       companyId: updateObject(fieldsState.companyId, { value: user.companyId }),
  //       userId: updateObject(fieldsState.userId, { value: user.userId })
  //       // password: updateObject(fieldsState.password, { value: user.password })
  //     })
  //     setFieldsState(newState)
  //   }
  // }

  useEffect(() => {
    if ((loginErrors !== null && loginErrors.length > 0) || showNotification) {
      ScrollTop()
    }
  }, [loginErrors, showNotification])

  return (
    <Content>

      <PageLoading loading={isAuthenticating} />
      {isLoginEnabled() && (
        <div className="login">
          <USBGrid gridGap="large" addClasses="PageGrid">
            <USBColumn largeSpan={9} />
            <USBColumn largeSpan={7} addClasses="table">
              <div style={{ width: '100%' }}>
                <USBGrid gridGap="large" addClasses="PageGrid">
                  <USBColumn largeSpan={15} addClasses="column">
                    <PageTitle
                      headerText={isCredentialRecovery ? 'Forgot Password' : 'Log In'}
                      addClasses="darkbg center"
                    />
                    {loginErrors !== null && loginErrors.length > 0 && (
                      <>
                        <br />
                        <PageWarning errors={loginErrors} />
                      </>
                    )}
                    {showNotification && (
                      <>
                        <br />
                        <PageWarning
                          notificationText={notificationText}
                          notificationVariant={notificationVariant}
                        />
                      </>
                    )}
                  </USBColumn>
                </USBGrid>
                {isCredentialRecovery ? (
                  <CredentialRecoveryPanel
                    loginFieldsState={loginFieldsState}
                    loginErrors={loginErrors}
                    setLoginErrors={setLoginErrors}
                    clearHandler={onClearClick}
                    cancelHandler={onCancelClick}
                    saveHandler={onSaveClick}
                    fieldsState={fieldsState}
                    setFieldState={setFieldState}
                    onSensitiveValueChange={setSensitiveCredentialValue}
                    getCompareToValue={getCredentialCompareToValue}
                    credentialResetCounter={credentialResetCounter}
                  />
                ) : (
                  <LoginComponent
                    loginFieldsState={loginFieldsState}
                    loginErrors={loginErrors}
                    setLoginErrors={setLoginErrors}
                    loginHandler={onLoginClick}
                    clearHandler={onClearClick}
                    fieldsState={fieldsState}
                    setFieldState={setFieldState}
                    onSensitiveValueChange={setSensitiveCredentialValue}
                    credentialResetCounter={credentialResetCounter}
                  />
                )}
              </div>
            </USBColumn>
          </USBGrid>
        </div>
      )}
    </Content>
  )
}

export default Login
